import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteCompanyEquipment from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyEquipment';
import AutocompleteCompanyProjects from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyProjects';
import AutocompleteCompanyServices from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyServices';
import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import TextInput from '~components/FormFields/v2/TextInput';
import { Account } from '~hooks/useAccount/models';
import { Rate } from '~hooks/useRates/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  RateCopyToFormDto,
  rateCopyToFormSchema,
  setRateCopyToFormDefaultValues,
} from './rateCopyToFormSchema';

interface RateCopyToFormProps {
  availableAccounts: Account[];
  errors?: ModelError_Item[];
  rate: Rate;
}

export type RateCopyToFormHandler = {
  onSubmit: () => Promise<RateCopyToFormDto | undefined>;
};

const RateCopyToForm = forwardRef<RateCopyToFormHandler, RateCopyToFormProps>(
  function RateCopyToForm({ availableAccounts, errors, rate }, ref) {
    const { userStore } = useStores();

    const companyId = getEffectiveUserCompanyId(userStore);

    const { control, handleSubmit, setError, clearErrors } = useForm<RateCopyToFormDto>({
      resolver: yupResolver(rateCopyToFormSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setRateCopyToFormDefaultValues(rate),
    });

    const accountFieldOptions = useMemo(() => {
      const options: AutocompleteFieldOption<Account>[] = availableAccounts.map(
        (account) => ({ label: account.name, value: account.id, _entity: account }),
      );

      return options;
    }, [availableAccounts]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (errors) {
        for (const error of errors) {
          if (error.field === 'name') {
            setError('name', { message: error.message });
          }
        }
      }

      return () => {
        clearErrors();
      };
    }, [errors, clearErrors, setError]);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />

        <AutocompleteField
          control={control}
          isRequired
          label={t('form_fields.account')}
          name="account"
          options={accountFieldOptions}
        />

        <AutocompleteCompanyProjects
          companyId={companyId}
          control={control}
          isMultiple
          label={t('form_fields.project')}
          limitTags={2}
          name="projects"
        />

        <AutocompleteCompanyEquipment
          companyId={companyId}
          control={control}
          groupBy={(item) => item._entity.typeName}
          isMultiple
          label={t('form_fields.equipment_type')}
          limitTags={2}
          name="equipments"
        />

        <AutocompleteCompanyServices
          companyId={companyId}
          control={control}
          groupBy={(item) => item._entity.type}
          isMultiple
          label={t('form_fields.service')}
          limitTags={2}
          name="services"
        />
      </Box>
    );
  },
);

export default RateCopyToForm;
